import { FAQ } from "../FAQ/index";
import InlineLink from "../inlineLink";

const DataIHaveIdeasSlackLink = () => (
  <InlineLink href={"https://oceanxmedia.slack.com/archives/C0374D6THLN"}>
    #data-i-have-ideas
  </InlineLink>
);

export const AISearchFAQ = () => {
  const copy = [
    {
      title: "What is the AI Image Search tool?",
      content: (
        <>
          <p>
            This tool is an early demonstration of <i>one</i> use case for
            AI-powered Computer Vision technologies being explored by the OceanX
            data team.
          </p>
          <br />
          <p>
            {" "}
            It functions similarly to Google Image Search. You can provide an
            image as your search query, and you will be shown images in our
            cloud storage which are most-similar to your search image. The
            results are rank-ordered, showing most-similar images first and
            less-similar images later.
          </p>
        </>
      ),
    },
    {
      title: "How do I use the search tool?",
      content: (
        <>
          <p>
            The easiest way to get started is to choose one of the example
            images as your search query.
          </p>
          <br />
          <p>
            Simply hover over one of the example images, then click{" "}
            {"Search on Image"} to get started!
          </p>
        </>
      ),
    },
    {
      title: "How can I search on other images, aside from the example images?",
      content: (
        <>
          <p>
            Currently, we support searching on a subset of images stored in
            Mako, including stills extracted from Chimaera ROV dive footage. We
            also support searching on Fishbase/Sealifebase content which is
            rehosted in Mako.
          </p>
          <br />
          <p>
            In the future, this tool will be expanded to support a broader range
            of content, including footage managed by the Media team, and custom
            images that you may upload or link to on an external site.
          </p>
          <br />
          <p>
            While the data team is hard at work at making these tools available
            in a self-service manner, we are happy to share that these use cases
            are already supported by the backend technology that powers this
            page!
          </p>
          <br />
          <p>
            If you want to explore custom searches today, please reach out to
            the Data team over Slack in <DataIHaveIdeasSlackLink />
          </p>
          <br />
          <p>
            We are excited to explore this new technology, and will need{" "}
            <b>YOUR</b> partnership to identify the most-impactful use cases
            within OceanX!
          </p>
        </>
      ),
    },
    {
      title: "What images are shown in the search results?",
      content: (
        <>
          <p>
            Your search results will contain images that have been processed by
            AI-powered search index. Currently, the search index includes stills
            extracted from{" "}
            <InlineLink href={"data/assets/search?query=sealog+hd+jpg"}>
              Chimaera ROV footage
            </InlineLink>
            and from a snapshot of the{" "}
            <InlineLink href={"data/assets/search?query=genus="}>
              Fishbase/Sealifebase
            </InlineLink>
            databases which we host in Mako.
          </p>
          <br />
          <p>
            We look forward to onboarding additional data sources in the future.
            If you have any suggestions, please reach out to the data team in{" "}
            <DataIHaveIdeasSlackLink />
          </p>
        </>
      ),
    },
    {
      title: (
        <p>
          How does the underlying AI technology that powers this search work?
        </p>
      ),
      content: (
        <>
          <p>
            We use a cutting-edge model trained and open-sourced by Meta,
            <InlineLink href={"https://dinov2.metademolab.com/"}>
              DinoV2,
            </InlineLink>
            as our foundational layer for this technology
          </p>
          <br />
          <p>
            DinoV2 is a{" "}
            <InlineLink href={"https://arxiv.org/abs/2010.11929"}>
              vision transformer
            </InlineLink>{" "}
            model. Similar to the large language models (LLMs) that power
            popular AI tools like ChatGPT, which are based on the related{" "}
            <InlineLink href={"https://arxiv.org/abs/1706.03762"}>
              text transformer architectures
            </InlineLink>
            , DinoV2 is trained a so-called self-supervised manner. This allows
            the model to be trained on huge volumes of data (142M images)
            without requiring human annotation. Through the training process,
            the model learns a general understanding of visual data which we can
            leverage for downstream tasks (like search).
          </p>
          <br />

          <p>
            To build our image search index, we run a process that downloads
            images from Mako and passes them to the DinoV2 model.
          </p>
          <br />
          <p>
            The DinoV2 model produces a large list of numbers, known as an{" "}
            <i>embedding vector</i>.
          </p>
          <br />
          <p>
            You can think of the embedding vector as a coordinate - like
            latitude and longitude. If we know the lat/lng coordinates for any
            two objects in the world, we can easily calculate the distance
            between those two coordinates. For example, if we know the
            coordinates OceanXplorer at any given time, and if we know the
            lat/lng coordinates of all the ports in the world, we can easily do
            a calculation to determine the closest port to the OceanXplorer.
          </p>
          <br />
          <p>
            This is almost exactly how our image search functions! The primary
            difference is that, instead of our {'"coordinates"'} being a vector
            of two elements (lat/lng) as in the OXR case, the DinoV2 model
            produces a vector with 1536 dimensions for each image we process. By
            finding the distance between the 1536-dimensional embedding vector
            generated for the searched-upon image and all other images in our
            database, we can find the most-similar images.
          </p>
          <br />
          <p>
            To learn more about this process, please check out the technical
            report on{" "}
            <InlineLink
              href={
                "https://www.notion.so/oceanxorg/Computer-Vision-Technical-Report-Q1-24-6dc5b9167bef4bda933ad3084fef48c7"
              }
            >
              Notion
            </InlineLink>
          </p>
        </>
      ),
    },
    {
      title:
        "Image search is cool and all - but what else can we do with this AI technology?",
      content: (
        <>
          <p>Great question!</p>
          <br />
          <p>
            There are many potential use cases for the AI model that we are
            currently leveraging for this task, as well as potential use case
            for other classes of models.
          </p>
          <br />
          <p>
            One exciting use case we have been exploring has been{" "}
            <i>image classification</i>. Essentially - given a frame of dive
            footage, can we automatically generate tags describing the image
            contents, and then make those searchable?
          </p>
          <br />
          <p>
            While we are still early in our exploration of image classification,
            we already have some encouraging early results. On a task to detect
            laser instrument activations from ROV dive footage, we have trained
            a model with 100% precision, 99% accuracy, 77% recall. You can read
            a deep-dive technical report on that custom model on{" "}
            <InlineLink
              href={
                "https://www.notion.so/oceanxorg/Computer-Vision-Technical-Report-Q1-24-6dc5b9167bef4bda933ad3084fef48c7"
              }
            >
              Notion
            </InlineLink>
          </p>
          <br />
          <p>
            If you have any ideas, thoughts, or suggestions, please do not
            hesitate to reach out to the Data team on Slack @{" "}
            <DataIHaveIdeasSlackLink />
          </p>
        </>
      ),
    },
  ];
  return <FAQ copy={copy} />;
};
